import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { NoonReportActivitiesSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionEnum, NoonReportTypeEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getNumberValue } from '../../../utils/aolFieldsHelper';
import OdfjellInputComponent from '../../common/odfjell-input-component/OdfjellInputComponent';
import { ActivitiesWrapper, MismatchHeader, MismatchWrapper } from './ActivitiesSection.style';
import { roundTo2Decimals } from '../../../utils/numberHelper';
import { ValidatedNoonReport } from '../../../types/ViewModel/ValidatedNoonReport';
import { prettifyHours } from 'odfjell-date-time-utils/dateTimeFormatter';
import { Duration } from 'luxon';

type ActivitiesVM = {
    showPortActivities?: boolean;
    dryDockingPortActivityHours?: number;
    loadingPortActivityHours?: number;
    unloadingPortActivityHours?: number;
    shiftingAndMooringPortActivityHours?: number;
    idlePortActivityHours?: number;
    anchorPortActivityHours?: number;
    steamingForTankCleaningPortActivityHours?: number;
    canalTransitPortActivityHours?: number;

    showOldSeaActivities?: boolean;
    awaitingOrderOrDriftingHours?: number;
    standardActivitiesAtSeaHours?: number;
    otherExclusionsHours?: number;
    unscheduledStoppagesHours?: number;
    speedOrderHours?: number;

    showOperationalActivities?: boolean;
    operationalTargetSpeedHours?: number;
    operationalTargetConsumptionHours?: number;
    operationalNonTargetHours?: number;
    operationalAwaitingOrdersOrDriftingHours?: number;
    operationalTankPreparationHours?: number;

    showNavigationalActivities?: boolean;
    navigationalEcaZoneHours?: number;
    navigationalCasualtyAndSarHours?: number;
    navigationalCasualtyAndSarDrillHours?: number;
    navigationalHighRiskAreaHours?: number;
    navigationalPilotManeuveringHours?: number;
    navigationalRiverPortManeuveringHours?: number;
    navigationalSafetyManeuveringHours?: number;
    navigationalStormShelteringHours?: number;
    navigationalTrafficManeuveringHours?: number;

    showTechnicalActivities?: boolean;
    technicalAuxiliarySystemFailureHours?: number;
    technicalFuelQualityIssueHours?: number;
    technicalPropulsionSystemFailureHours?: number;
    technicalOtherTechnicalIssueHours?: number;

    reportType?: NoonReportTypeEnum;
    sectionData?: NoonReportSectionData | undefined;

    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

const ActivitiesSectionComponent: React.FC = () => {
    const { selectedAolGroup, selectedAolDataItem } = useAolStore();
    const [aolItemVM, setAolItemVM] = useState<ActivitiesVM>({});
    const [activitiesVM, setActivitiesVM] = useState<ActivitiesVM>({});
    const [mismatchedActivities, setMismatchedActivities] = useState<any[]>([]);
    const { saveAolEvent, saveSection, triggerSaveEvent } = useAolSaveHandler();

    useEffect(() => {
        if (!selectedAolDataItem) return;

        if (!activitiesVM.isInitialized) {
            initVM(selectedAolDataItem.noonReport);
        }
    }, [selectedAolDataItem]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.Activities &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') {
                initVM(saveAolEvent.aolDataItem.noonReport);
            }
        }
    }, [saveAolEvent]);

    const initVM = (noonReport?: ValidatedNoonReport) => {
        if (!selectedAolDataItem) return;
        let noonReportVM: ActivitiesVM | undefined;
        let aolItemVM: ActivitiesVM | undefined;

        if (noonReport?.activitiesSection) {
            noonReportVM = {
                dryDockingPortActivityHours: getNumberValue(noonReport.activitiesSection.dryDockingPortActivityHours),
                loadingPortActivityHours: getNumberValue(noonReport.activitiesSection.loadingPortActivityHours),
                unloadingPortActivityHours: getNumberValue(noonReport.activitiesSection.unloadingPortActivityHours),
                shiftingAndMooringPortActivityHours: getNumberValue(noonReport.activitiesSection.shiftingAndMooringPortActivityHours),
                idlePortActivityHours: getNumberValue(noonReport.activitiesSection.idlePortActivityHours),
                anchorPortActivityHours: getNumberValue(noonReport.activitiesSection.anchorPortActivityHours),
                steamingForTankCleaningPortActivityHours: getNumberValue(noonReport.activitiesSection.steamingForTankCleaningPortActivityHours),
                canalTransitPortActivityHours: getNumberValue(noonReport.activitiesSection.canalTransitPortActivityHours),

                awaitingOrderOrDriftingHours: getNumberValue(noonReport.activitiesSection.awaitingOrderOrDriftingHours),
                standardActivitiesAtSeaHours: getNumberValue(noonReport.activitiesSection.standardActivitiesAtSeaHours),
                otherExclusionsHours: getNumberValue(noonReport.activitiesSection.otherExclusionsHours),
                unscheduledStoppagesHours: getNumberValue(noonReport.activitiesSection.unscheduledStoppagesHours),
                speedOrderHours: getNumberValue(noonReport.activitiesSection.speedOrderHours),

                operationalTargetSpeedHours: getNumberValue(noonReport.activitiesSection.operationalTargetSpeedHours),
                operationalTargetConsumptionHours: getNumberValue(noonReport.activitiesSection.operationalTargetConsumptionHours),
                operationalNonTargetHours: getNumberValue(noonReport.activitiesSection.operationalNonTargetHours),
                operationalAwaitingOrdersOrDriftingHours: getNumberValue(noonReport.activitiesSection.operationalAwaitingOrdersOrDriftingHours),
                operationalTankPreparationHours: getNumberValue(noonReport.activitiesSection.operationalTankPreparationHours),

                navigationalEcaZoneHours: getNumberValue(noonReport.activitiesSection.navigationalEcaZoneHours),
                navigationalCasualtyAndSarHours: getNumberValue(noonReport.activitiesSection.navigationalCasualtyAndSarHours),
                navigationalHighRiskAreaHours: getNumberValue(noonReport.activitiesSection.navigationalHighRiskAreaHours),
                navigationalPilotManeuveringHours: getNumberValue(noonReport.activitiesSection.navigationalPilotManeuveringHours),
                navigationalRiverPortManeuveringHours: getNumberValue(noonReport.activitiesSection.navigationalRiverPortManeuveringHours),
                navigationalSafetyManeuveringHours: getNumberValue(noonReport.activitiesSection.navigationalSafetyManeuveringHours),
                navigationalStormShelteringHours: getNumberValue(noonReport.activitiesSection.navigationalStormShelteringHours),
                navigationalTrafficManeuveringHours: getNumberValue(noonReport.activitiesSection.navigationalTrafficManeuveringHours),
                navigationalCasualtyAndSarDrillHours: getNumberValue(noonReport.activitiesSection.navigationalCasualtyAndSarDrillHours),

                technicalAuxiliarySystemFailureHours: getNumberValue(noonReport.activitiesSection.technicalAuxiliarySystemFailureHours),
                technicalFuelQualityIssueHours: getNumberValue(noonReport.activitiesSection.technicalFuelQualityIssueHours),
                technicalPropulsionSystemFailureHours: getNumberValue(noonReport.activitiesSection.technicalPropulsionSystemFailureHours),
                technicalOtherTechnicalIssueHours: getNumberValue(noonReport.activitiesSection.technicalOtherTechnicalIssueHours),

                reportType: noonReport.metadata.noonReportType,
                sectionData: noonReport.activitiesSection.sectionData,
                notValidFieldsArray: [],
                hasInvalidFields: false,
                isInitialized: true,
            };

            updateShowActivities(noonReportVM);
        }
        if (selectedAolDataItem?.aolItem) {
            const aolItem = selectedAolDataItem.aolItem;

            aolItemVM = {
                dryDockingPortActivityHours: getTimeZoneAdjustedHours(aolItem.dryDockingTime),
                loadingPortActivityHours: getTimeZoneAdjustedHours(aolItem.loadingTimes),
                unloadingPortActivityHours: getTimeZoneAdjustedHours(aolItem.unloadingTimes),
                shiftingAndMooringPortActivityHours: getTimeZoneAdjustedHours(aolItem.shiftingMooringTime),
                idlePortActivityHours: getTimeZoneAdjustedHours(aolItem.idleTime),
                anchorPortActivityHours: getTimeZoneAdjustedHours(aolItem.anchorageTime),
                steamingForTankCleaningPortActivityHours: getTimeZoneAdjustedHours(aolItem.steamingForTankCleaningTime),
                canalTransitPortActivityHours: getTimeZoneAdjustedHours(aolItem.canalTransitTime),

                operationalTargetSpeedHours: getTimeZoneAdjustedHours(aolItem.operationalTargetSpeedTime),
                operationalTargetConsumptionHours: getTimeZoneAdjustedHours(aolItem.operationalTargetConsumptionTime),
                operationalNonTargetHours: getTimeZoneAdjustedHours(aolItem.operationalNonTargetTime),
                operationalAwaitingOrdersOrDriftingHours: getTimeZoneAdjustedHours(aolItem.operationalAwaitingOrdersOrDriftingTime),
                operationalTankPreparationHours:
                    aolItem.portLogAolItem.noonReportType === NoonReportTypeEnum.Sea
                        ? getTimeZoneAdjustedHours(aolItem.operationalTankPreparationTime)
                        : getTimeZoneAdjustedHours(aolItem.tankCleaningTime),

                navigationalEcaZoneHours: getTimeZoneAdjustedHours(aolItem.navigationalEcaZoneTime),
                navigationalCasualtyAndSarHours: getTimeZoneAdjustedHours(aolItem.navigationalCasualtyAndSarTime),
                navigationalHighRiskAreaHours: getTimeZoneAdjustedHours(aolItem.navigationalHighRiskAreaTime),
                navigationalPilotManeuveringHours: getTimeZoneAdjustedHours(aolItem.navigationalPilotManeuveringTime),
                navigationalRiverPortManeuveringHours: getTimeZoneAdjustedHours(aolItem.navigationalRiverPortManeuveringTime),
                navigationalSafetyManeuveringHours: getTimeZoneAdjustedHours(aolItem.navigationalSafetyManeuveringTime),
                navigationalStormShelteringHours: getTimeZoneAdjustedHours(aolItem.navigationalStormShelteringTime),
                navigationalTrafficManeuveringHours: getTimeZoneAdjustedHours(aolItem.navigationalTrafficManeuveringTime),
                navigationalCasualtyAndSarDrillHours: getTimeZoneAdjustedHours(aolItem.navigationalCasualtyAndSarDrillTime),

                technicalAuxiliarySystemFailureHours: getTimeZoneAdjustedHours(aolItem.technicalAuxiliarySystemFailureTime),
                technicalFuelQualityIssueHours: getTimeZoneAdjustedHours(aolItem.technicalFuelQualityIssueTime),
                technicalOtherTechnicalIssueHours: getTimeZoneAdjustedHours(aolItem.technicalOtherTechnicalIssueTime),
                technicalPropulsionSystemFailureHours: getTimeZoneAdjustedHours(aolItem.technicalPropulsionSystemFailureTime),

                reportType: aolItem.portLogAolItem.noonReportType,
                sectionData: noonReport?.activitiesSection?.sectionData ?? createSectionData(NoonReportSectionEnum.Activities),
            };

            updateShowActivities(aolItemVM);

            selectedAolDataItem.aolDataItemProperties.isDryDockingAolReport = isDryDockingAolReport(aolItemVM);
            selectedAolDataItem.aolDataItemProperties.hasTankPreparationActivity = hasTankPreparationActivity(aolItemVM);
            selectedAolDataItem.aolDataItemProperties.tankCleaningHours = getTankCleaningHours(aolItemVM);
            selectedAolDataItem.aolDataItemProperties.consumptionOrderHours = aolItemVM.operationalTargetConsumptionHours ?? 0;
            selectedAolDataItem.aolDataItemProperties.speedOrderHours = aolItemVM.operationalTargetSpeedHours ?? 0;
            selectedAolDataItem.aolDataItemProperties.nonTargetOrderHours = aolItemVM.operationalNonTargetHours ?? 0;
            selectedAolDataItem.aolDataItemProperties.shiftingAndMooringPortActivityHours = aolItemVM.shiftingAndMooringPortActivityHours ?? 0;

            setAolItemVM({ ...aolItemVM });
        }

        if (noonReportVM) setActivitiesVM({ ...noonReportVM });
        else if (aolItemVM) setActivitiesVM({ ...aolItemVM });

        if (noonReportVM && aolItemVM) {
            checkPortLogDifference(noonReportVM, aolItemVM);
        }
    };

    const getTimeZoneAdjustedHours = (duration: Duration) => {
        if (!selectedAolDataItem?.aolItem) return 0;
        const activityHours = duration.shiftTo('hours').hours;
        if (activityHours === 0) return 0;
        if (activityHours < 24) return activityHours;

        const aolItem = selectedAolDataItem.aolItem;
        const timeZoneChangeHours = aolItem.timeZoneChangeHours;
        const dstOffsetHours = aolItem.dstOffsetHours;
        const adjustedHours = activityHours + timeZoneChangeHours + dstOffsetHours;

        return adjustedHours > 0 ? adjustedHours : 0;
    };

    const isDryDockingAolReport = (vm: ActivitiesVM): boolean => {
        return vm.dryDockingPortActivityHours === roundTo2Decimals(selectedAolDataItem?.reportHours);
    };
    const hasTankPreparationActivity = (vm: ActivitiesVM): boolean => {
        return (
            (vm.operationalTankPreparationHours !== undefined && vm.operationalTankPreparationHours > 0) ||
            (vm.steamingForTankCleaningPortActivityHours !== undefined && vm.steamingForTankCleaningPortActivityHours > 0)
        );
    };
    const getTankCleaningHours = (vm: ActivitiesVM): number => {
        return (vm.operationalTankPreparationHours ?? 0) + (vm.steamingForTankCleaningPortActivityHours ?? 0);
    };

    const updateShowActivities = (vm: ActivitiesVM) => {
        vm.showPortActivities =
            vm.dryDockingPortActivityHours > 0 ||
            vm.loadingPortActivityHours > 0 ||
            vm.unloadingPortActivityHours > 0 ||
            vm.shiftingAndMooringPortActivityHours > 0 ||
            vm.idlePortActivityHours > 0 ||
            vm.anchorPortActivityHours > 0 ||
            vm.steamingForTankCleaningPortActivityHours > 0 ||
            vm.canalTransitPortActivityHours > 0 ||
            vm.operationalTankPreparationHours > 0;

        vm.showOldSeaActivities =
            vm.awaitingOrderOrDriftingHours > 0 ||
            vm.standardActivitiesAtSeaHours > 0 ||
            vm.otherExclusionsHours > 0 ||
            vm.unscheduledStoppagesHours > 0 ||
            vm.speedOrderHours > 0;

        vm.showOperationalActivities =
            vm.operationalTargetSpeedHours > 0 ||
            vm.operationalTargetConsumptionHours > 0 ||
            vm.operationalNonTargetHours > 0 ||
            vm.operationalAwaitingOrdersOrDriftingHours > 0 ||
            vm.operationalTankPreparationHours > 0;

        vm.showNavigationalActivities =
            vm.navigationalEcaZoneHours > 0 ||
            vm.navigationalCasualtyAndSarHours > 0 ||
            vm.navigationalHighRiskAreaHours > 0 ||
            vm.navigationalPilotManeuveringHours > 0 ||
            vm.navigationalRiverPortManeuveringHours > 0 ||
            vm.navigationalSafetyManeuveringHours > 0 ||
            vm.navigationalStormShelteringHours > 0 ||
            vm.navigationalTrafficManeuveringHours > 0 ||
            vm.navigationalCasualtyAndSarDrillHours > 0;

        vm.showTechnicalActivities =
            vm.technicalAuxiliarySystemFailureHours > 0 ||
            vm.technicalPropulsionSystemFailureHours > 0 ||
            vm.technicalFuelQualityIssueHours > 0 ||
            vm.technicalOtherTechnicalIssueHours > 0;
    };

    const checkPortLogDifference = (noonReportVM: ActivitiesVM, aolItemVM: ActivitiesVM) => {
        const mismatchedActivities: any = [];
        if (!selectedAolDataItem) {
            setMismatchedActivities([...mismatchedActivities]);
            return;
        }

        const operationalTankPreparation = hasDifferentValues(noonReportVM, aolItemVM, 'operationalTankPreparationHours');
        if (operationalTankPreparation) mismatchedActivities.push(operationalTankPreparation);

        if (selectedAolDataItem.reportType !== NoonReportTypeEnum.Sea) {
            const dryDocking = hasDifferentValues(noonReportVM, aolItemVM, 'dryDockingPortActivityHours');
            const loading = hasDifferentValues(noonReportVM, aolItemVM, 'loadingPortActivityHours');
            const unloading = hasDifferentValues(noonReportVM, aolItemVM, 'unloadingPortActivityHours');
            const shiftingAndMooring = hasDifferentValues(noonReportVM, aolItemVM, 'shiftingAndMooringPortActivityHours');
            const idle = hasDifferentValues(noonReportVM, aolItemVM, 'idlePortActivityHours');
            const anchor = hasDifferentValues(noonReportVM, aolItemVM, 'anchorPortActivityHours');
            const steamingForTankCleaning = hasDifferentValues(noonReportVM, aolItemVM, 'steamingForTankCleaningPortActivityHours');
            const canalTransit = hasDifferentValues(noonReportVM, aolItemVM, 'canalTransitPortActivityHours');

            if (dryDocking) mismatchedActivities.push(dryDocking);
            if (loading) mismatchedActivities.push(loading);
            if (unloading) mismatchedActivities.push(unloading);
            if (shiftingAndMooring) mismatchedActivities.push(shiftingAndMooring);
            if (idle) mismatchedActivities.push(idle);
            if (anchor) mismatchedActivities.push(anchor);
            if (steamingForTankCleaning) mismatchedActivities.push(steamingForTankCleaning);
            if (canalTransit) mismatchedActivities.push(canalTransit);
        }
        if (selectedAolDataItem.reportType === NoonReportTypeEnum.Sea) {
            const operationalTargetSpeed = hasDifferentValues(noonReportVM, aolItemVM, 'operationalTargetSpeedHours');
            const operationalTargetConsumption = hasDifferentValues(noonReportVM, aolItemVM, 'operationalTargetConsumptionHours');
            const operationalNonTarget = hasDifferentValues(noonReportVM, aolItemVM, 'operationalNonTargetHours');
            const operationalAwaitingOrdersOrDrifting = hasDifferentValues(noonReportVM, aolItemVM, 'operationalAwaitingOrdersOrDriftingHours');

            const navigationalEcaZone = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalEcaZoneHours');
            const navigationalCasualtyAndSar = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalCasualtyAndSarHours');
            const navigationalHighRiskArea = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalHighRiskAreaHours');
            const navigationalPilotManeuvering = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalPilotManeuveringHours');
            const navigationalRiverPortManeuvering = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalRiverPortManeuveringHours');
            const navigationalSafetyManeuvering = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalSafetyManeuveringHours');
            const navigationalStormSheltering = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalStormShelteringHours');
            const navigationalTrafficManeuvering = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalTrafficManeuveringHours');
            const navigationalCasualtyAndSarDrill = hasDifferentValues(noonReportVM, aolItemVM, 'navigationalCasualtyAndSarDrillHours');

            const technicalAuxiliarySystemFailure = hasDifferentValues(noonReportVM, aolItemVM, 'technicalAuxiliarySystemFailureHours');
            const technicalFuelQualityIssue = hasDifferentValues(noonReportVM, aolItemVM, 'technicalFuelQualityIssueHours');
            const technicalPropulsionSystemFailure = hasDifferentValues(noonReportVM, aolItemVM, 'technicalPropulsionSystemFailureHours');
            const technicalOtherTechnicalIssue = hasDifferentValues(noonReportVM, aolItemVM, 'technicalOtherTechnicalIssueHours');

            if (operationalTargetSpeed) mismatchedActivities.push(operationalTargetSpeed);
            if (operationalTargetConsumption) mismatchedActivities.push(operationalTargetConsumption);
            if (operationalNonTarget) mismatchedActivities.push(operationalNonTarget);
            if (operationalAwaitingOrdersOrDrifting) mismatchedActivities.push(operationalAwaitingOrdersOrDrifting);

            if (navigationalEcaZone) mismatchedActivities.push(navigationalEcaZone);
            if (navigationalCasualtyAndSar) mismatchedActivities.push(navigationalCasualtyAndSar);
            if (navigationalHighRiskArea) mismatchedActivities.push(navigationalHighRiskArea);
            if (navigationalPilotManeuvering) mismatchedActivities.push(navigationalPilotManeuvering);
            if (navigationalRiverPortManeuvering) mismatchedActivities.push(navigationalRiverPortManeuvering);
            if (navigationalSafetyManeuvering) mismatchedActivities.push(navigationalSafetyManeuvering);
            if (navigationalStormSheltering) mismatchedActivities.push(navigationalStormSheltering);
            if (navigationalTrafficManeuvering) mismatchedActivities.push(navigationalTrafficManeuvering);
            if (navigationalCasualtyAndSarDrill) mismatchedActivities.push(navigationalCasualtyAndSarDrill);

            if (technicalAuxiliarySystemFailure) mismatchedActivities.push(technicalAuxiliarySystemFailure);
            if (technicalFuelQualityIssue) mismatchedActivities.push(technicalFuelQualityIssue);
            if (technicalPropulsionSystemFailure) mismatchedActivities.push(technicalPropulsionSystemFailure);
            if (technicalOtherTechnicalIssue) mismatchedActivities.push(technicalOtherTechnicalIssue);
        }

        setMismatchedActivities([...mismatchedActivities]);
    };

    const hasDifferentValues = (noonReportVM: ActivitiesVM, aolItemVM: ActivitiesVM, param: string) => {
        const noonReportValue = noonReportVM[param] !== undefined ? +noonReportVM[param].toFixed(2) : 0;
        const aolItemValue = aolItemVM[param] !== undefined ? +aolItemVM[param].toFixed(2) : 0;
        if (noonReportValue !== aolItemValue) {
            return { param, noonReportValue: noonReportVM[param], aolItemValue: aolItemVM[param] };
        }
    };

    const getDescription = (param: string) => {
        if (param === 'dryDockingPortActivityHours') return 'DD / Yard';
        if (param === 'loadingPortActivityHours') return 'Loading';
        if (param === 'unloadingPortActivityHours') return 'Unloading';
        if (param === 'shiftingAndMooringPortActivityHours') return 'River or port maneuvering';
        if (param === 'idlePortActivityHours') return 'No loading/unloading operation';
        if (param === 'anchorPortActivityHours') return 'Anchor';
        if (param === 'steamingForTankCleaningPortActivityHours') return 'Steaming for tank cleaning';
        if (param === 'canalTransitPortActivityHours') return 'Canal transit';

        if (param === 'standardActivitiesAtSeaHours') return 'Standard activities at sea';
        if (param === 'awaitingOrderOrDriftingHours') return 'Awaiting orders or drifting';
        if (param === 'otherExclusionsHours') return 'Other exclusions';
        if (param === 'unscheduledStoppagesHours') return 'Unscheduled stoppages';
        if (param === 'speedOrderHours') return 'Hours on speed order';

        if (param === 'operationalTargetSpeedHours') return 'Speed order';
        if (param === 'operationalTargetConsumptionHours') return 'Consumption order';
        if (param === 'operationalNonTargetHours') return 'Non-target voyage';
        if (param === 'operationalAwaitingOrdersOrDriftingHours') return 'Awaiting orders or drifting';
        if (param === 'operationalTankPreparationHours') return 'Tank preparation';

        if (param === 'navigationalEcaZoneHours') return 'ECA zone at sea';
        if (param === 'navigationalCasualtyAndSarHours') return 'Casualty & SAR';
        if (param === 'navigationalHighRiskAreaHours') return 'High risk area';
        if (param === 'navigationalPilotManeuveringHours') return 'Pilot maneuvering';
        if (param === 'navigationalRiverPortManeuveringHours') return 'River or port maneuvering';
        if (param === 'navigationalSafetyManeuveringHours') return 'Safety maneuvering';
        if (param === 'navigationalStormShelteringHours') return 'Storm sheltering';
        if (param === 'navigationalTrafficManeuveringHours') return 'Traffic maneuvering';
        if (param === 'navigationalCasualtyAndSarDrillHours') return 'Casualty & SAR (Drill / Exercise)';

        if (param === 'technicalAuxiliarySystemFailureHours') return 'Auxiliary system failure';
        if (param === 'technicalFuelQualityIssueHours') return 'Fuel quality issue';
        if (param === 'technicalPropulsionSystemFailureHours') return 'Propulsion system failure';
        if (param === 'technicalOtherTechnicalIssueHours') return 'Other technical issue';

        return '';
    };

    const onAcceptValuesClicked = () => {
        if (!selectedAolDataItem) return;
        setActivitiesVM({ ...aolItemVM });
        triggerSaveEvent('save_section', NoonReportSectionEnum.Activities, selectedAolDataItem);
    };

    const save = () => {
        const vm = mismatchedActivities.length > 0 ? aolItemVM : activitiesVM;
        setActivitiesVM({ ...aolItemVM });
        const data = new NoonReportActivitiesSectionFactory().createBM(vm);

        if (!vm.hasInvalidFields) {
            saveSection(data);
        }
    };

    return (
        <>
            {selectedAolGroup && selectedAolDataItem && (
                <ActivitiesWrapper>
                    {(selectedAolGroup.aolVersion < 3 || mismatchedActivities.length === 0) && (
                        <>
                            {((selectedAolDataItem.reportType !== NoonReportTypeEnum.Sea && !activitiesVM.showPortActivities) ||
                                (selectedAolDataItem.reportType === NoonReportTypeEnum.Sea &&
                                    !activitiesVM.showOldSeaActivities &&
                                    !activitiesVM.showNavigationalActivities &&
                                    !activitiesVM.showOperationalActivities &&
                                    !activitiesVM.showTechnicalActivities)) && (
                                <>
                                    <div className="category">No activities</div>
                                </>
                            )}
                            {selectedAolDataItem.reportType !== NoonReportTypeEnum.Sea && activitiesVM.showPortActivities && (
                                <>
                                    <div className="category">In port:</div>
                                    <OdfjellInputComponent
                                        type={'duration'}
                                        isReadOnly={true}
                                        description={getDescription('dryDockingPortActivityHours')}
                                        value={activitiesVM.dryDockingPortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('loadingPortActivityHours')}
                                        value={activitiesVM.loadingPortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('unloadingPortActivityHours')}
                                        value={activitiesVM.unloadingPortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('shiftingAndMooringPortActivityHours')}
                                        value={activitiesVM.shiftingAndMooringPortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('idlePortActivityHours')}
                                        value={activitiesVM.idlePortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('anchorPortActivityHours')}
                                        value={activitiesVM.anchorPortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('steamingForTankCleaningPortActivityHours')}
                                        value={activitiesVM.steamingForTankCleaningPortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('canalTransitPortActivityHours')}
                                        value={activitiesVM.canalTransitPortActivityHours}
                                        hideIfEmpty={true}
                                    />
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'duration'}
                                        description={getDescription('operationalTankPreparationHours')}
                                        value={activitiesVM.operationalTankPreparationHours}
                                        hideIfEmpty={true}
                                    />
                                </>
                            )}
                            {selectedAolDataItem.reportType === NoonReportTypeEnum.Sea && (
                                <>
                                    {activitiesVM.showOldSeaActivities && (
                                        <>
                                            <div className="category">At sea:</div>
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('standardActivitiesAtSeaHours')}
                                                value={activitiesVM.standardActivitiesAtSeaHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('awaitingOrderOrDriftingHours')}
                                                value={activitiesVM.awaitingOrderOrDriftingHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('otherExclusionsHours')}
                                                value={activitiesVM.otherExclusionsHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('unscheduledStoppagesHours')}
                                                value={activitiesVM.unscheduledStoppagesHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('speedOrderHours')}
                                                value={activitiesVM.speedOrderHours}
                                                hideIfEmpty={true}
                                            />
                                        </>
                                    )}
                                    {activitiesVM.showOperationalActivities && (
                                        <>
                                            <div className="category">Operational</div>
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('operationalTargetConsumptionHours')}
                                                value={activitiesVM.operationalTargetConsumptionHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('operationalTargetSpeedHours')}
                                                value={activitiesVM.operationalTargetSpeedHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('operationalNonTargetHours')}
                                                value={activitiesVM.operationalNonTargetHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('operationalAwaitingOrdersOrDriftingHours')}
                                                value={activitiesVM.operationalAwaitingOrdersOrDriftingHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('operationalTankPreparationHours')}
                                                value={activitiesVM.operationalTankPreparationHours}
                                                hideIfEmpty={true}
                                            />
                                        </>
                                    )}
                                    {activitiesVM.showNavigationalActivities && (
                                        <>
                                            <div className="category">Navigational</div>
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalEcaZoneHours')}
                                                value={activitiesVM.navigationalEcaZoneHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalCasualtyAndSarHours')}
                                                value={activitiesVM.navigationalCasualtyAndSarHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalHighRiskAreaHours')}
                                                value={activitiesVM.navigationalHighRiskAreaHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalPilotManeuveringHours')}
                                                value={activitiesVM.navigationalPilotManeuveringHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalRiverPortManeuveringHours')}
                                                value={activitiesVM.navigationalRiverPortManeuveringHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalSafetyManeuveringHours')}
                                                value={activitiesVM.navigationalSafetyManeuveringHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalStormShelteringHours')}
                                                value={activitiesVM.navigationalStormShelteringHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalTrafficManeuveringHours')}
                                                value={activitiesVM.navigationalTrafficManeuveringHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('navigationalCasualtyAndSarDrillHours')}
                                                value={activitiesVM.navigationalCasualtyAndSarDrillHours}
                                                hideIfEmpty={true}
                                            />
                                        </>
                                    )}
                                    {activitiesVM.showTechnicalActivities && (
                                        <>
                                            <div className="category">Technical</div>
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('technicalAuxiliarySystemFailureHours')}
                                                value={activitiesVM.technicalAuxiliarySystemFailureHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('technicalPropulsionSystemFailureHours')}
                                                value={activitiesVM.technicalPropulsionSystemFailureHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('technicalFuelQualityIssueHours')}
                                                value={activitiesVM.technicalFuelQualityIssueHours}
                                                hideIfEmpty={true}
                                            />
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'duration'}
                                                description={getDescription('technicalOtherTechnicalIssueHours')}
                                                value={activitiesVM.technicalOtherTechnicalIssueHours}
                                                hideIfEmpty={true}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {selectedAolGroup.aolVersion >= 3 && mismatchedActivities.length > 0 && (
                        <MismatchWrapper>
                            <div>
                                <br />
                                The following activities are not in sync with the reported events:
                                <br />
                                <MismatchHeader>
                                    <div className="first-cell">&nbsp;</div>
                                    <div>AOL</div>
                                    <div>Events</div>
                                </MismatchHeader>
                                <div>
                                    {mismatchedActivities.map((activity, i) => {
                                        return (
                                            <MismatchHeader key={`${activity.param}_${i}`}>
                                                <div className="first-cell">{getDescription(activity.param)}</div>
                                                <div>{prettifyHours(activity.noonReportValue)}</div>
                                                <div>{prettifyHours(activity.aolItemValue)}</div>
                                            </MismatchHeader>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="reject-or-accept-buttons">
                                <button onClick={() => onAcceptValuesClicked()}>Accept</button>
                            </div>
                        </MismatchWrapper>
                    )}
                </ActivitiesWrapper>
            )}
        </>
    );
};

export default ActivitiesSectionComponent;
